
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SearchableSelect from "../../../components/shared/SearchableSelect";

const SampleDownloadModal = ({ 
  open, 
  onOpenChange, 
  userCategories, 
  companyCategories 
}) => {
  const validationSchema = Yup.object({
    category: Yup.string().required("Category is required"),
    subCategory: Yup.string().required("Sub-category is required"),
  });

  const excelHeaders = [
    "S No",
    "Question",
    "Option_1",
    "Option_2",
    "Option_3",
    "Option_4",
    "Option_5",
    "Answer",
    "Category",
    "Sub_category",
    "Tags",
    "Difficulty",
    "Points",
  ];

  const downloadSample = () => {
    const selectedCategory =
      userCategories.find((cat) => cat.name === Formik.values.category) ||
      companyCategories.find((cat) => cat.name === Formik.values.category);

    const excelValues = [
      {
        "S No": 1,
        Question: "What is react.js",
        Category: Formik.values.category,
        Sub_category: Formik.values.subCategory,
        Option_1: "A JavaScript library for building user interfaces",
        Option_2: "A backend framework for Node.js",
        Option_3: "A database management tool",
        Option_4: "A CSS preprocessor",
        Option_5: "A browser extension for debugging JavaScript",
        Answer: "Option_1",
        Tags: "Frontend",
        Difficulty: "Easy",
        Points: 1,
      },
      {
        "S No": 2,
        Question: "What is Html",
        Category: Formik.values.category,
        Sub_category: Formik.values.subCategory,
        Option_1: "A programming language",
        Option_2: "A database management system",
        Option_3: "A markup language used to structure content on the web",
        Option_4: "A JavaScript framework",
        Option_5: "A server-side scripting language",
        Answer: "Option_3",
        Tags: "Frontend",
        Difficulty: "Medium",
        Points: 2,
      },
      {
        "S No": 3,
        Question: "What is Node",
        Category: Formik.values.category,
        Sub_category: Formik.values.subCategory,
        Option_1: "A JavaScript framework for frontend development",
        Option_2: "A type of database used in NoSQL systems",
        Option_3: "A browser extension for debugging JavaScript",
        Option_4: "A CSS library for building responsive layouts",
        Option_5:
          "A runtime environment that allows JavaScript to run on the server",
        Answer: "Option_5",
        Tags: "Backend",
        Difficulty: "Hard",
        Points: 3,
      },
    ];

    const workSheetData = [
      excelHeaders,
      ...excelValues.map((obj) =>
        excelHeaders.map((Headers) => obj[Headers] || "")
      ),
    ];
    const workSheet = XLSX.utils.aoa_to_sheet(workSheetData);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Question Banks");

    const xlsxBlob = XLSX.write(workBook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([xlsxBlob], { type: "application/octet-stream" });
    onOpenChange(false)
    saveAs(blob, "sampleQuestionBank.xlsx");
  };

  const Formik = useFormik({
    initialValues: { category: "", subCategory: "" },
    validationSchema: validationSchema,
    onSubmit: downloadSample,
  });

  const renderSearchableSelectField = (
    name,
    value,
    onValueChange,
    placeholder,
    userItems,
    companyItems,
    error,
    touched
  ) => (
    <div>
      <Label htmlFor={name}>{placeholder}*</Label>
      <SearchableSelect
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        userItems={userItems}
        companyItems={companyItems}
      />
      {touched && error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onOpenChange(false);
        Formik.resetForm();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download Sample Excel Sheet</DialogTitle>
          <DialogDescription>
            Download a new excel sheet for sample.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={Formik.handleSubmit}>
          <div className="grid gap-4 py-4 mt-2">
            <div className="grid grid-row-2 gap-4">
              {renderSearchableSelectField(
                "category",
                Formik.values.category,
                (value) => Formik.setFieldValue("category", value),
                "Category",
                userCategories,
                companyCategories,
                Formik.errors.category,
                Formik.touched.category
              )}

              {renderSearchableSelectField(
                "subCategory",
                Formik.values.subCategory,
                (value) => Formik.setFieldValue("subCategory", value),
                "Sub-Category",
                (() => {
                  const selectedCategory = userCategories.find(
                    (cat) => cat.name === Formik.values.category
                  );
                  return selectedCategory ? selectedCategory.subCategories : [];
                })(),
                (() => {
                  const selectedCategory = companyCategories.find(
                    (cat) => cat.name === Formik.values.category
                  );
                  return selectedCategory ? selectedCategory.subCategories : [];
                })(),
                Formik.errors.subCategory,
                Formik.touched.subCategory
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">Download Excel Sample</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SampleDownloadModal;
